import { Button, Input, MenuItem, Select, Switch, Text, VSpacer } from '@/components/DesignSystem';
import { useAddRetailer, useEditRetailer } from '@/hooks/useRetailer';
import { ApiRetailer, UpdateRetailer } from '@api/interfaces';
import { RetailerEndpoint } from '@api/endpoints/RetailerEndpoint';
import { Alert, Dialog, DialogActions, DialogContent, Stack } from '@mui/material';
import { Country } from '@shared/enums/Country';
import { FeatureFlag } from '@shared/enums/FeatureFlag';
import { getObjectChanges, trimValue } from '@shared/utilities';
import { ChangeEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFeatureFlags } from '@/hooks/useFeatureFlag';

type FormInputs = Pick<
  ApiRetailer,
  'isActive' | 'isErpIntegrationEnabled' | 'name' | 'isChurnEnabled' | 'country'
>;

type RetailerFormDialogProps = {
  initialValue?: FormInputs;
  isAddRetailerOpen: boolean;
  setIsAddRetailerOpen: (isAddRetailerOpen: boolean) => void;
};
const RetailerFormDialog = ({
  isAddRetailerOpen,
  initialValue = {
    isActive: true,
    isErpIntegrationEnabled: false,
    isChurnEnabled: false,
    name: '',
    country: Country.UnitedStates,
  },
  setIsAddRetailerOpen,
}: RetailerFormDialogProps) => {

  const [retailer, setRetailer] = useState(initialValue);
  const [error, setError] = useState('');
  const { isFlagOn } = useFeatureFlags();

  const { id } = useParams();

  const isEditDisabled = Object.entries(retailer).every(
    ([key, value]: [string, string | boolean | undefined | null]) =>
      initialValue[key as keyof FormInputs] === value,
  );
  const isEditing = !!initialValue.name;
  const isCanadianAdaptationsEnabled = isFlagOn(FeatureFlag.CanadianAdaptations);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'name') {
      setError('');
    }
    setRetailer({
      ...retailer,
      [e.target.name]: e.target.value.trimStart(),
    });
  };

  const onClose = () => {
    setIsAddRetailerOpen(false);
  };

  const { addRetailer, isLoading } = useAddRetailer(setIsAddRetailerOpen, setError);
  const { editRetailer, isLoading: isEditLoading } = useEditRetailer(
    id ?? '',
    setIsAddRetailerOpen,
    setError,
  );

  const onSave = async () => {
    const trimmedInputs = Object.fromEntries(
      Object.entries(retailer).map(([key, nestedValue]) =>
        [key, trimValue(nestedValue)]),
    ) as FormInputs;

    const changedInputs = getObjectChanges(
      initialValue,
      trimmedInputs,
    ) as UpdateRetailer;

    if (id) {
      editRetailer(changedInputs);
    } else {
      const createRequest: RetailerEndpoint.Create.Request = {
        ...changedInputs,
        name: changedInputs?.name?.toString() ?? '',
        ...(isCanadianAdaptationsEnabled && retailer.country ? { country: retailer.country } : {}),
      };
      
      addRetailer(createRequest);
    }
  };

  return (
    <Dialog fullWidth onClose={onClose} open={isAddRetailerOpen}>
      <DialogContent>
        <Stack spacing={3}>
          {!!error && (
            <Alert color='error' icon={false}>{error}</Alert>
          )}
          <Text category='h6'>{isEditing ? 'Edit' : 'Add'} Retailer</Text>
          <Input
            label='Organization name'
            name='name'
            onChange={onInputChange}
            required
            testID='hierarchy-retailer-name'
            value={retailer.name}
          />
          {isCanadianAdaptationsEnabled && (
            <Select
              disabled={isEditing}
              label="Country"
              onChangeValue={(value) => setRetailer({
                ...retailer,
                country: value as Country,
              })}
              required
              testID='hierarchy-retailer-country'
              value={retailer.country}
            >
              <MenuItem testID='country-united-states' value={Country.UnitedStates}>
                {Country.UnitedStates}
              </MenuItem>
              <MenuItem testID='country-canada' value={Country.Canada}>{Country.Canada}</MenuItem>
            </Select>
          )}
          <Switch
            checked={retailer.isErpIntegrationEnabled}
            labelPlacement="start"
            onChangeChecked={() => setRetailer({
              ...retailer,
              isErpIntegrationEnabled: !retailer.isErpIntegrationEnabled,
            })}
            sx={{ justifyContent: 'left' }}
            testID="retailer-is-erp-integration-enabled-switch"
          >
            Enable the ERP integration for this retailer
          </Switch>
          <Switch
            checked={retailer.isChurnEnabled}
            labelPlacement="start"
            onChangeChecked={() => setRetailer({
              ...retailer,
              isChurnEnabled: !retailer.isChurnEnabled,
            })}
            sx={{ justifyContent: 'left' }}
            testID="retailer-is-churn-customer-enabled-switch"
          >
            Enable customer churn for this retailer
          </Switch>
        </Stack>
      </DialogContent>
      <VSpacer size='8' />
      <DialogActions>
        <Button
          disabled={isLoading || isEditLoading}
          onClick={onClose}
          testID='hierarchy-retailer-cancel'
          variant='text'
        >
          Cancel
        </Button>
        <Button
          disabled={!retailer?.name
            || isEditDisabled || isLoading || isEditLoading || !!error}
          loading={isLoading || isEditLoading}
          onClick={onSave}
          testID='hierarchy-retailer-save'
        >
          Save
        </Button>
      </DialogActions>
      <VSpacer size='5' />
    </Dialog>
  );
};

export default RetailerFormDialog;
